import { useEffect, useMemo, useState } from 'react';
import { api } from '../../lib/api';
import { useAsync } from '../../lib/useAsync';
import { useToast } from '../../lib/toast';
import { Banner, EmptyState, Field, LoadingPage, Tag } from '../../components/ui';
import { Metric, Slider, pctLabel, usd } from './shared';

interface Line {
  id: string;
  fund: 'operating' | 'replacement';
  kind: 'income' | 'expense';
  group: string | null;
  label: string;
  classification: string;
  annual: number;
}

interface Assumptions {
  monthlyDues: number;
  assessableUnits: number;
  billingFrequency: 'monthly' | 'quarterly' | 'semiannual' | 'annual';
  collectionRate: number;
  collectionLagMonths: number;
  expenseInflation: number;
  contingencyPct: number;
  reserveContributionMonthly: number;
  otherReceiptsMonthlyOverride: number | null;
  specialAssessments: Array<{ name: string; totalAmount: number; startMonth: string; months: number; fund: 'operating' | 'replacement' }>;
  projects: Array<{ name: string; amount: number; month: string; fund: 'operating' | 'replacement' }>;
  contracts: Array<{ vendorName: string; category: string; monthlyAmount: number }>;
  target: { kind: 'cash-neutral' } | { kind: 'minimum-cash'; amount?: number } | { kind: 'year-end-cash'; amount: number };
}

interface BreakEvenView {
  targetDescription: string;
  monthlyDues: number | null;
  monthlyDuesRoundedUp: number | null;
  currentDues: number;
  changeFromCurrent: number | null;
  accountsFor: string[];
  monthlyBilledAtBreakEven: number | null;
  annualBilledAtBreakEven: number | null;
}

interface Result {
  monthlyDues: number;
  monthlyBilled: number;
  annualAssessmentBillings: number;
  expectedCashCollections: number;
  otherCashReceipts: number;
  operatingRevenue: number;
  operatingExpenses: number;
  reserveContributions: number;
  oneTimeExpenditures: number;
  operatingSurplus: number;
  yearEndOperatingCash: number;
  yearEndReserveCash: number;
  startOperatingCash: number;
  minimumMonthlyCash: { key: string; label: string; amount: number };
  firstShortfallLabel: string | null;
  firstBelowMinimumLabel: string | null;
  notes: string[];
}

const CLASS_LABEL: Record<string, string> = {
  regular_assessment: 'Regular assessments',
  special_assessment: 'Special assessments',
  reserve_allocation: 'Reserve allocation',
  other_income: 'Other income',
  operating_expense: 'Operating expense',
  capital: 'Capital / one-time',
  bad_debt: 'Bad debt',
};

export function BudgetPlanner() {
  const { notify, fail } = useToast();
  const list = useAsync(() => api.finBudgets(), []);
  const [openId, setOpenId] = useState<string | null>(null);
  const [compareIds, setCompareIds] = useState<string[]>([]);
  const [compare, setCompare] = useState<Record<string, unknown> | null>(null);
  const data = list.data as {
    scenarios: Array<{ id: string; name: string; fiscalYear: number; source: string; monthlyDues: number | null; notes: string | null }>;
    approved: { reportLabel: string; operatingIncome: number; operatingExpenses: number };
    planning: { earliestFiscalYear: number; asOf: string; units: number | null; dues: number | null };
  } | null;

  if (list.error && /Approve a monthly statement/.test(list.error)) {
    return <EmptyState title="Approve a monthly statement first." hint="Draft budgets start from the approved statement, never by overwriting it." />;
  }
  if (!data) return list.loading ? <LoadingPage label="Loading budget scenarios…" /> : null;

  if (openId) {
    return <BudgetEditor id={openId} onBack={() => { setOpenId(null); void list.reload(); }} />;
  }

  async function create(source: 'approved' | 'actuals' | 'zero') {
    const name = window.prompt('Name this scenario', source === 'zero' ? 'Zero-based' : source === 'actuals' ? 'From last year actuals' : 'From approved budget');
    if (!name) return;
    try {
      const { id } = await api.finBudgetCreate({ name, source, fiscalYear: data!.planning.earliestFiscalYear });
      notify('Scenario created. It does not change the approved budget.', 'success');
      setOpenId(id);
    } catch (error) {
      fail(error, 'Could not create that scenario.');
    }
  }

  return (
    <div>
      <div className="card">
        <div className="card-head"><h3>Budget planner</h3></div>
        <p className="mut">
          Drafts for fiscal years that have not started yet. Editing a scenario never overwrites the approved budget on the {data.approved.reportLabel} statement
          (operating income {usd(data.approved.operatingIncome)}, operating expenses {usd(data.approved.operatingExpenses)}).
        </p>
        <div className="btn-row">
          <button type="button" className="btn" onClick={() => void create('approved')}>Start from approved budget</button>
          <button type="button" className="btn" onClick={() => void create('actuals')}>Start from prior-year actuals</button>
          <button type="button" className="btn" onClick={() => void create('zero')}>Zero-based</button>
        </div>
        <ImportBudget year={data.planning.earliestFiscalYear} onCreated={(id) => setOpenId(id)} />
      </div>

      {data.scenarios.length === 0 ? (
        <EmptyState title="No draft scenarios yet." hint="Create one above. $45, $55 and $60 dues can be compared inside a scenario." />
      ) : (
        <div className="card tw">
          <h3>Scenarios</h3>
          <table>
            <thead><tr><th /><th>Name</th><th>Fiscal year</th><th>Started from</th><th className="num">Monthly dues</th><th /></tr></thead>
            <tbody>
              {data.scenarios.map((s) => (
                <tr key={s.id}>
                  <td>
                    <input
                      type="checkbox"
                      aria-label={`Compare ${s.name}`}
                      checked={compareIds.includes(s.id)}
                      onChange={(e) => setCompareIds((ids) => (e.target.checked ? [...ids, s.id].slice(0, 5) : ids.filter((x) => x !== s.id)))}
                    />
                  </td>
                  <td>{s.name}{s.notes && <div className="mut">{s.notes}</div>}</td>
                  <td>FY{s.fiscalYear}</td>
                  <td className="mut">{s.source}</td>
                  <td className="num">{usd(s.monthlyDues)}</td>
                  <td className="num">
                    <button type="button" className="btn s" onClick={() => setOpenId(s.id)}>Open</button>{' '}
                    <a className="btn s" href={api.finBudgetExportUrl(s.id)}>Export</a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="btn-row">
            <button
              type="button"
              className="btn"
              disabled={compareIds.length < 2}
              onClick={async () => {
                try { setCompare(await api.finBudgetCompare(compareIds)); }
                catch (error) { fail(error, 'Could not compare those scenarios.'); }
              }}
            >
              Compare selected
            </button>
          </div>
        </div>
      )}

      {compare && <CompareCard data={compare} onClose={() => setCompare(null)} />}
    </div>
  );
}

function ImportBudget({ year, onCreated }: { year: number; onCreated: (id: string) => void }) {
  const { notify, fail } = useToast();
  const [name, setName] = useState('');
  return (
    <div style={{ marginTop: 12 }}>
      <p className="mut">Import a CSV or .xlsx with Label and Annual columns.</p>
      <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
        <input placeholder="Imported scenario name" aria-label="Imported scenario name" value={name} onChange={(e) => setName(e.target.value)} style={{ maxWidth: 240 }} />
        <input
          type="file"
          accept=".csv,.xlsx"
          aria-label="Budget file"
          onChange={async (e) => {
            const file = e.target.files?.[0];
            e.target.value = '';
            if (!file || !name.trim()) {
              fail(new Error('Name the scenario first, then choose a file.'), 'Name the scenario first.');
              return;
            }
            try {
              const result = await api.finBudgetImport(file, name.trim(), year);
              notify(result.skipped.length ? `Imported, skipped ${result.skipped.length} rows.` : 'Imported.', 'success');
              onCreated(result.id);
            } catch (error) {
              fail(error, 'That file could not be imported.');
            }
          }}
        />
      </div>
    </div>
  );
}

function CompareCard({ data, onClose }: { data: Record<string, unknown>; onClose: () => void }) {
  const rows = (data.scenarios as Array<{ id: string; name: string; fiscalYear: number; result: Result; breakEven: { monthlyDuesRoundedUp: number | null } }>) ?? [];
  return (
    <div className="card tw">
      <div className="card-head"><h3>Side by side</h3><button type="button" className="btn s ghost" onClick={onClose}>Close</button></div>
      <table>
        <thead>
          <tr>
            <th />
            {rows.map((r) => <th key={r.id}>{r.name}<div className="mut">FY{r.fiscalYear}</div></th>)}
          </tr>
        </thead>
        <tbody>
          {([
            ['Monthly dues', (r: Result) => usd(r.monthlyDues, true)],
            ['Monthly billed (theoretical)', (r: Result) => usd(r.monthlyBilled)],
            ['Annual billed (theoretical)', (r: Result) => usd(r.annualAssessmentBillings)],
            ['Expected cash collections', (r: Result) => usd(r.expectedCashCollections)],
            ['Operating expenses', (r: Result) => usd(r.operatingExpenses)],
            ['Reserve contributions', (r: Result) => usd(r.reserveContributions)],
            ['One-time spending', (r: Result) => usd(r.oneTimeExpenditures)],
            ['Operating surplus / (deficit)', (r: Result) => usd(r.operatingSurplus)],
            ['Year-end operating cash', (r: Result) => usd(r.yearEndOperatingCash)],
            ['Year-end reserve cash', (r: Result) => usd(r.yearEndReserveCash)],
            ['Lowest monthly cash', (r: Result) => `${usd(r.minimumMonthlyCash.amount)} (${r.minimumMonthlyCash.label})`],
            ['Cash shortfall month', (r: Result) => r.firstShortfallLabel ?? 'None'],
          ] as Array<[string, (r: Result) => string]>).map(([label, get]) => (
            <tr key={label}>
              <td>{label}</td>
              {rows.map((r) => <td key={r.id} className="num">{get(r.result)}</td>)}
            </tr>
          ))}
          <tr>
            <td>Break-even monthly dues</td>
            {rows.map((r) => <td key={r.id} className="num">{usd(r.breakEven.monthlyDuesRoundedUp)}</td>)}
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function BudgetEditor({ id, onBack }: { id: string; onBack: () => void }) {
  const { notify, fail } = useToast();
  const loaded = useAsync(() => api.finBudget(id), [id]);
  const pack = loaded.data as {
    scenario: { id: string; name: string; source: string; notes: string | null; fiscalYear: number; assumptions: Assumptions; lines: Line[] };
    result: Result;
    breakEven: BreakEvenView;
    provisional: string[];
  } | null;
  const [name, setName] = useState('');
  const [assumptions, setAssumptions] = useState<Assumptions | null>(null);
  const [lines, setLines] = useState<Line[]>([]);
  const [live, setLive] = useState<{ result: Result; breakEven: BreakEvenView } | null>(null);
  const [duesTable, setDuesTable] = useState<Array<Record<string, unknown>> | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!pack) return;
    setName(pack.scenario.name);
    setAssumptions(pack.scenario.assumptions);
    setLines(pack.scenario.lines);
    setLive({ result: pack.result, breakEven: pack.breakEven });
  }, [pack]);

  const body = useMemo(
    () => (assumptions ? { fiscalYear: pack?.scenario.fiscalYear, assumptions, lines } : null),
    [assumptions, lines, pack?.scenario.fiscalYear],
  );

  useEffect(() => {
    if (!body) return undefined;
    const timer = window.setTimeout(() => {
      void api
        .finBudgetEvaluate(body)
        .then((r) => setLive({ result: r.result as unknown as Result, breakEven: r.breakEven as unknown as BreakEvenView }))
        .catch(() => undefined);
    }, 280);
    return () => window.clearTimeout(timer);
  }, [body]);

  if (!pack || !assumptions) return loaded.loading ? <LoadingPage label="Opening the scenario…" /> : <button type="button" className="btn s" onClick={onBack}>Back</button>;
  const result = live?.result ?? pack.result;
  const be = live?.breakEven ?? pack.breakEven;
  const a = assumptions;
  const setA = (patch: Partial<Assumptions>) => setAssumptions({ ...a, ...patch });

  return (
    <div>
      <div className="row between" style={{ marginBottom: 12 }}>
        <button type="button" className="btn s ghost" onClick={onBack}>← All scenarios</button>
        <div className="btn-row" style={{ margin: 0 }}>
          <a className="btn s" href={api.finBudgetExportUrl(id)}>Export</a>
          <button
            type="button"
            className="btn s"
            onClick={async () => {
              const copy = window.prompt('Name for the copy', `${name} copy`);
              if (!copy) return;
              try {
                const created = await api.finBudgetDuplicate(id, copy);
                notify('Duplicated. The approved budget is unchanged.', 'success');
                onBack();
                // parent will show the list; user can open the copy.
                void created;
              } catch (error) { fail(error, 'Could not duplicate.'); }
            }}
          >
            Duplicate
          </button>
          <button
            type="button"
            className="btn"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              try {
                await api.finBudgetSave(id, { name, scenario: body });
                notify('Scenario saved. The approved budget was not changed.', 'success');
              } catch (error) { fail(error, 'Could not save.'); }
              finally { setBusy(false); }
            }}
          >
            Save scenario
          </button>
        </div>
      </div>

      <div className="card">
        <Field label="Scenario name"><input value={name} onChange={(e) => setName(e.target.value)} maxLength={80} /></Field>
        <p className="mut">Started from {pack.scenario.source}. Fiscal year {pack.scenario.fiscalYear}. Changes update the projection immediately and only apply when you save.</p>
        {pack.provisional.length > 0 && <Banner kind="warn">Provisional: {pack.provisional.join(' ')}</Banner>}
      </div>

      <div className="card">
        <div className="card-head"><h3>Results</h3>{result.firstShortfallLabel && <Tag tone="bad">Shortfall {result.firstShortfallLabel}</Tag>}</div>
        <div className="stat-grid">
          <Metric label="Monthly billed (theoretical)" value={usd(result.monthlyBilled)} hint={`${a.assessableUnits} homes × ${usd(a.monthlyDues, true)}`} />
          <Metric label="Annual billed (theoretical)" value={usd(result.annualAssessmentBillings)} />
          <Metric label="Expected cash collections" value={usd(result.expectedCashCollections)} hint={`${pctLabel(a.collectionRate, 0)} collection rate`} estimate />
          <Metric label="Operating revenue" value={usd(result.operatingRevenue)} />
          <Metric label="Operating expenses" value={usd(result.operatingExpenses)} />
          <Metric label="Reserve contributions" value={usd(result.reserveContributions)} />
          <Metric label="One-time expenditures" value={usd(result.oneTimeExpenditures)} />
          <Metric label="Operating surplus / (deficit)" value={usd(result.operatingSurplus)} tone={result.operatingSurplus < 0 ? 'bad' : 'ok'} />
          <Metric label="Year-end operating cash" value={usd(result.yearEndOperatingCash)} estimate />
          <Metric label="Year-end reserve cash" value={usd(result.yearEndReserveCash)} estimate />
          <Metric label="Lowest monthly cash" value={usd(result.minimumMonthlyCash.amount)} hint={result.minimumMonthlyCash.label} estimate />
          <Metric label="Cash shortfall month" value={result.firstShortfallLabel ?? 'None'} tone={result.firstShortfallLabel ? 'bad' : 'ok'} estimate />
        </div>
        {result.notes.map((n) => <p className="mut" key={n} style={{ margin: '4px 0' }}>{n}</p>)}
      </div>

      <div className="card">
        <h3>Assumptions</h3>
        <div className="grid two">
          <Slider label="Monthly dues" min={0} max={200} step={1} value={a.monthlyDues} display={usd(a.monthlyDues, true)} onChange={(v) => setA({ monthlyDues: v })} />
          <Slider label="Assessable homes" min={0} max={2000} step={1} value={a.assessableUnits} display={String(a.assessableUnits)} onChange={(v) => setA({ assessableUnits: v })} />
          <Slider label="Expected collection rate" min={0} max={1} step={0.01} value={a.collectionRate} display={pctLabel(a.collectionRate, 0)} onChange={(v) => setA({ collectionRate: v })} />
          <Slider label="Collection lag (months)" min={0} max={6} step={1} value={a.collectionLagMonths} display={String(a.collectionLagMonths)} onChange={(v) => setA({ collectionLagMonths: v })} />
          <Slider label="Expense inflation" min={-0.05} max={0.2} step={0.005} value={a.expenseInflation} display={pctLabel(a.expenseInflation, 1)} onChange={(v) => setA({ expenseInflation: v })} />
          <Slider label="Contingency" min={0} max={0.2} step={0.01} value={a.contingencyPct} display={pctLabel(a.contingencyPct, 0)} onChange={(v) => setA({ contingencyPct: v })} />
          <Field label="Reserve contribution per month ($)">
            <input type="number" value={a.reserveContributionMonthly} onChange={(e) => setA({ reserveContributionMonthly: Number(e.target.value) })} />
          </Field>
          <Field label="Billing frequency">
            <select value={a.billingFrequency} onChange={(e) => setA({ billingFrequency: e.target.value as Assumptions['billingFrequency'] })}>
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
              <option value="semiannual">Twice a year</option>
              <option value="annual">Annually</option>
            </select>
          </Field>
        </div>
      </div>

      <div className="card">
        <div className="card-head"><h3>Break-even dues</h3></div>
        <p>{be.targetDescription}</p>
        <div className="stat-grid">
          <Metric label="Lowest dues that meet the target" value={usd(be.monthlyDues, true)} estimate />
          <Metric label="Rounded up to a whole dollar" value={usd(be.monthlyDuesRoundedUp, true)} estimate />
          <Metric label="Change from current dues" value={usd(be.changeFromCurrent, true)} />
          <Metric label="Annual billed at that rate" value={usd(be.annualBilledAtBreakEven)} />
        </div>
        <p className="mut">This is not expenses divided by homes. It accounts for: {be.accountsFor.join('; ')}.</p>
      </div>

      <div className="card tw">
        <div className="card-head">
          <h3>$45 / $55 / $60 comparison</h3>
          <button
            type="button"
            className="btn s"
            onClick={async () => {
              if (!body) return;
              try { setDuesTable((await api.finBudgetDuesComparison(body, [45, 55, 60])).options); }
              catch (error) { fail(error, 'Could not compare those rates.'); }
            }}
          >
            Calculate
          </button>
        </div>
        <p className="mut">Theoretical billings before delinquency, exemptions, adjustments or collection timing. 679 homes at these rates bill $30,555 / $37,345 / $40,740 a month.</p>
        {duesTable && (
          <table>
            <thead>
              <tr>
                <th>Monthly dues</th>
                <th className="num">Monthly billed</th>
                <th className="num">Annual billed</th>
                <th className="num">Expected cash</th>
                <th className="num">Year-end operating cash</th>
                <th>Shortfall</th>
              </tr>
            </thead>
            <tbody>
              {duesTable.map((row) => (
                <tr key={String(row.dues)}>
                  <td>{usd(row.dues as number, true)}</td>
                  <td className="num">{usd(row.monthlyBilled as number)}</td>
                  <td className="num">{usd(row.annualAssessmentBillings as number)}</td>
                  <td className="num">{usd(row.expectedCashCollections as number)}</td>
                  <td className="num">{usd(row.yearEndOperatingCash as number)}</td>
                  <td>{(row.firstShortfallLabel as string | null) ?? 'None'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="card tw">
        <div className="card-head">
          <h3>Budget lines</h3>
          <button
            type="button"
            className="btn s"
            onClick={() => setLines((cur) => [...cur, { id: `line-${Date.now()}`, fund: 'operating', kind: 'expense', group: null, label: 'New line', classification: 'operating_expense', annual: 0 }])}
          >
            Add line
          </button>
        </div>
        <table>
          <thead><tr><th>Line</th><th>Fund</th><th>Type</th><th>Class</th><th className="num">Annual</th><th /></tr></thead>
          <tbody>
            {lines.map((l, index) => (
              <tr key={l.id}>
                <td><input value={l.label} onChange={(e) => setLines(lines.map((x, i) => (i === index ? { ...x, label: e.target.value } : x)))} /></td>
                <td>
                  <select value={l.fund} onChange={(e) => setLines(lines.map((x, i) => (i === index ? { ...x, fund: e.target.value as Line['fund'] } : x)))}>
                    <option value="operating">Operating</option>
                    <option value="replacement">Replacement</option>
                  </select>
                </td>
                <td>
                  <select value={l.kind} onChange={(e) => setLines(lines.map((x, i) => (i === index ? { ...x, kind: e.target.value as Line['kind'] } : x)))}>
                    <option value="income">Income</option>
                    <option value="expense">Expense</option>
                  </select>
                </td>
                <td>
                  <select value={l.classification} onChange={(e) => setLines(lines.map((x, i) => (i === index ? { ...x, classification: e.target.value } : x)))}>
                    {Object.entries(CLASS_LABEL).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
                  </select>
                </td>
                <td className="num"><input type="number" value={l.annual} onChange={(e) => setLines(lines.map((x, i) => (i === index ? { ...x, annual: Number(e.target.value) } : x)))} /></td>
                <td className="num"><button type="button" className="btn tiny ghost" onClick={() => setLines(lines.filter((_, i) => i !== index))}>Remove</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <PlansEditor assumptions={a} onChange={setA} />

      <button
        type="button"
        className="btn s danger"
        onClick={async () => {
          if (!window.confirm(`Delete “${name}”? The approved budget is not affected.`)) return;
          try { await api.finBudgetDelete(id); notify('Deleted.', 'success'); onBack(); }
          catch (error) { fail(error, 'Could not delete.'); }
        }}
      >
        Delete scenario
      </button>
    </div>
  );
}

function PlansEditor({ assumptions: a, onChange }: { assumptions: Assumptions; onChange: (next: Assumptions) => void }) {
  const [projName, setProjName] = useState('');
  const [projAmt, setProjAmt] = useState('');
  const [projMonth, setProjMonth] = useState('');
  return (
    <div className="card">
      <h3>Planned capital projects in this scenario</h3>
      <p className="mut">These are planning figures for the draft year. They do not change recorded actuals.</p>
      {a.projects.length > 0 && (
        <table>
          <tbody>
            {a.projects.map((p, i) => (
              <tr key={`${p.name}-${i}`}>
                <td>{p.name}<div className="mut">{p.month} · {p.fund}</div></td>
                <td className="num">{usd(p.amount)}</td>
                <td className="num"><button type="button" className="btn tiny ghost" onClick={() => onChange({ ...a, projects: a.projects.filter((_, j) => j !== i) })}>Remove</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <div className="row" style={{ gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <Field label="Name"><input value={projName} onChange={(e) => setProjName(e.target.value)} /></Field>
        <Field label="Amount ($)"><input value={projAmt} inputMode="decimal" onChange={(e) => setProjAmt(e.target.value)} /></Field>
        <Field label="Month (YYYY-MM)"><input value={projMonth} placeholder="2027-03" onChange={(e) => setProjMonth(e.target.value)} /></Field>
        <button
          type="button"
          className="btn s"
          disabled={!projName.trim() || Number(projAmt) <= 0 || !/^\d{4}-(0[1-9]|1[0-2])$/.test(projMonth)}
          onClick={() => {
            onChange({ ...a, projects: [...a.projects, { name: projName.trim(), amount: Number(projAmt), month: projMonth, fund: 'replacement' }] });
            setProjName(''); setProjAmt(''); setProjMonth('');
          }}
        >
          Add project
        </button>
      </div>
    </div>
  );
}
